import { Input, Select } from "@design";
import { useTranslation } from "react-i18next";
import type { FlowScheduleCreate } from "@/api";
import { Text } from "@/components/Text";

/**
 * The schedule editor for a workflow trigger, ported from the old UI's
 * FlowScheduler: daily / weekly (pick days) / custom (every N days), a run-at
 * time, and a schedule preview. Emits the structured `FlowScheduleCreate` the
 * backend expects. Timezone is fixed to UTC for now (the old UI offered a list;
 * the backend stores it, so this can be re-added without a backend change).
 */

export type ScheduleType = "daily" | "weekly" | "custom";

export interface ScheduleValue {
  scheduleType: ScheduleType;
  runAtTime: string;
  daysOfWeek: number[];
  intervalDays: number;
}

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const; // 0-6, Mon-Sun (backend)

const byNumber = (a: number, b: number) => a - b;

export const DEFAULT_SCHEDULE: ScheduleValue = {
  scheduleType: "daily",
  runAtTime: "09:00",
  daysOfWeek: [0, 1, 2, 3, 4], // weekdays
  intervalDays: 1,
};

/** Read the editable value out of an existing trigger's schedule (for edit). */
export function scheduleToValue(
  schedule?: {
    scheduleType?: string;
    runAtTime?: string;
    daysOfWeek?: number[] | null;
    intervalDays?: number | null;
  } | null,
): ScheduleValue {
  return {
    scheduleType: (schedule?.scheduleType as ScheduleType) ?? "daily",
    runAtTime: schedule?.runAtTime ?? "09:00",
    daysOfWeek: schedule?.daysOfWeek ?? [0, 1, 2, 3, 4],
    intervalDays: schedule?.intervalDays ?? 1,
  };
}

/** Build the backend payload. Weekly sends days; custom sends intervalDays. */
export function valueToSchedule(v: ScheduleValue): FlowScheduleCreate {
  return {
    scheduleType: v.scheduleType,
    runAtTime: v.runAtTime,
    timezone: "UTC",
    ...(v.scheduleType === "weekly" ? { daysOfWeek: [...v.daysOfWeek].sort(byNumber) } : {}),
    ...(v.scheduleType === "custom" ? { intervalDays: Math.max(1, v.intervalDays) } : {}),
  };
}

export function ScheduleFields({
  value,
  onChange,
}: Readonly<{ value: ScheduleValue; onChange: (next: ScheduleValue) => void }>) {
  const { t } = useTranslation("workflows");
  const set = (patch: Partial<ScheduleValue>) => onChange({ ...value, ...patch });

  // The schedule preview, via the namespace-bound t (typed i18n keys).
  const previewText = (() => {
    const time = value.runAtTime;
    if (value.scheduleType === "daily") return t("triggers.preview.daily", { time });
    if (value.scheduleType === "weekly") {
      const days = [...value.daysOfWeek]
        .sort(byNumber)
        .map((d) => t(`triggers.weekdays.${WEEKDAY_KEYS[d]}`))
        .join(", ");
      return t("triggers.preview.weekly", { days, time });
    }
    return t("triggers.preview.custom", { count: Math.max(1, value.intervalDays), time });
  })();

  const toggleDay = (day: number) =>
    set({
      daysOfWeek: value.daysOfWeek.includes(day)
        ? value.daysOfWeek.filter((d) => d !== day)
        : [...value.daysOfWeek, day],
    });

  return (
    <div className="flex flex-col gap-4">
      <Select
        aria-label={t("triggers.scheduleType")}
        options={[
          { value: "daily", label: t("triggers.schedule.daily") },
          { value: "weekly", label: t("triggers.schedule.weekly") },
          { value: "custom", label: t("triggers.schedule.custom") },
        ]}
        value={value.scheduleType}
        onChange={(v) => set({ scheduleType: v as ScheduleType })}
      />

      {value.scheduleType === "weekly" && (
        <div className="flex flex-col gap-2">
          <Text variant="body-sm" tone="secondary">
            {t("triggers.days")}
          </Text>
          <div className="flex gap-1.5">
            {WEEKDAY_KEYS.map((key, day) => {
              const selected = value.daysOfWeek.includes(day);
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleDay(day)}
                  className={`flex-1 rounded-lg px-2 py-1.5 [font:var(--type-caption)] transition-colors btn-focus-ring ${
                    selected
                      ? "bg-content-primary text-content-inverse"
                      : "bg-surface-secondary text-content-tertiary hover:bg-interactive-hover"
                  }`}
                >
                  {t(`triggers.weekdays.${key}`)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {value.scheduleType === "custom" && (
        <div className="flex items-center gap-2">
          <Text variant="body-sm" tone="secondary">
            {t("triggers.every")}
          </Text>
          <div className="w-20">
            <Input
              aria-label={t("triggers.intervalDays")}
              type="number"
              min={1}
              value={String(value.intervalDays)}
              onChange={(e) => set({ intervalDays: Math.max(1, Number(e.target.value) || 1) })}
            />
          </div>
          <Text variant="body-sm" tone="secondary">
            {t("triggers.daysUnit")}
          </Text>
        </div>
      )}

      <Input
        aria-label={t("triggers.runAt")}
        type="time"
        value={value.runAtTime}
        onChange={(e) => set({ runAtTime: e.target.value })}
      />

      <Text variant="caption" tone="tertiary">
        {previewText}
      </Text>
    </div>
  );
}
